import React, { useEffect, useState } from 'react';
import {
  Alert,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  Modal,
  Radio,
  Row,
  Space,
  Statistic,
  Tag,
  Typography
} from 'antd';
import { Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Layout from '@/common/Layout';
import styles from '@/common/Layout/index.module.less';
import FormItem from '@/common/FormItem';
import CustomButton from '@/common/CustomButton';
import CustomModal from '@/common/CustomModal';
import CustomTable from 'common/Content/CustomTable';
import useServerPagination from '@/hooks/useServerPagination';
import { getLoggedInUser } from 'utilities/helpers';
import { requestMaintenanceStatusRefresh } from '@/common/Layout/components/MaintenanceBanner';
import {
  getMaintenanceCurrent,
  getMaintenanceHistory,
  startMaintenance,
  extendMaintenance,
  endMaintenance
} from '../../redux/slice';
import {
  formatCountdown,
  formatClockTime,
  formatDateTime,
  formatMinutes
} from '../../maintenanceFormat';

const { TextArea } = Input;
const { Text } = Typography;

const REFRESH_MS = 30 * 1000;
const REASON_MAX_LENGTH = 500;
const DEFAULT_COUNTDOWN_MINUTES = 60;

const STATUS_TAGS = {
  active: { color: 'processing', label: 'Active' },
  cancelled: { color: 'default', label: 'Cancelled' },
  ended: { color: 'green', label: 'Ended' }
};

const ACTION_LABELS = {
  start: 'Started',
  extend: 'Extended',
  cancel: 'Cancelled',
  end: 'Ended',
  sessions_terminated: 'Users logged out'
};

const MaintenanceMode = () => {
  const { user } = getLoggedInUser();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const {
    current,
    currentReceivedAt,
    getCurrentLoading,
    history,
    pagination,
    getHistoryLoading,
    updateLoading
  } = useSelector((state) => state.maintenance);
  const { page, pageSize, onChange } = useServerPagination();

  const [now, setNow] = useState(Date.now());
  const [endModal, setEndModal] = useState(false);
  const [endRemarks, setEndRemarks] = useState('');

  const isAdmin = user?.role === 'admin';
  const window_ = current?.window;
  const countdownOptions = current?.countdown_options || [30, 60, 90, 120];
  const extendStep = current?.extend_step_minutes || 30;
  const remaining = window_
    ? Math.max(0, window_.seconds_remaining - (now - currentReceivedAt) / 1000)
    : 0;
  const cutoffReached = !!window_ && (window_.cutoff_passed || remaining <= 0);

  const loadCurrent = () => dispatch(getMaintenanceCurrent());

  const loadHistory = () =>
    dispatch(getMaintenanceHistory({ page, per_page: pageSize }));

  const reload = () => {
    loadCurrent();
    loadHistory();
    // Update this admin's own banner straight away.
    requestMaintenanceStatusRefresh();
  };

  useEffect(() => {
    if (!isAdmin) return undefined;
    loadCurrent();
    const refresh = setInterval(loadCurrent, REFRESH_MS);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(refresh);
      clearInterval(tick);
    };
  }, []);

  useEffect(() => {
    if (isAdmin) loadHistory();
  }, [page, pageSize]);

  if (!isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  const onStart = (values) => {
    const minutes = values.countdown_minutes;
    const logoutAt = new Date(Date.now() + minutes * 60 * 1000);
    Modal.confirm({
      title: 'Start maintenance mode?',
      centered: true,
      okText: 'Start Maintenance',
      okButtonProps: { danger: true },
      content: (
        <>
          <p>
            <b>{current?.logged_in_count || 0}</b> logged-in user(s) will see a
            countdown and will be logged out at{' '}
            <b>{formatClockTime(logoutAt)}</b> (in {formatMinutes(minutes)}).
          </p>
          <p className="mb-0">
            After that, only admins can sign in until you end maintenance.
          </p>
        </>
      ),
      onOk: () =>
        dispatch(
          startMaintenance({
            reason: values.reason,
            countdown_minutes: minutes
          })
        ).then((res) => {
          if (res?.payload?.success) {
            form.resetFields();
            reload();
          }
        })
    });
  };

  const onExtend = () => {
    const newLogoutAt = new Date(
      new Date(window_.logout_at).getTime() + extendStep * 60 * 1000
    );
    Modal.confirm({
      title: `Extend by ${formatMinutes(extendStep)}?`,
      centered: true,
      okText: 'Extend',
      content: `Users will now be logged out at ${formatClockTime(
        newLogoutAt
      )} instead of ${formatClockTime(window_.logout_at)}.`,
      onOk: () =>
        dispatch(extendMaintenance({})).then((res) => {
          if (res?.payload?.success) reload();
        })
    });
  };

  const onEnd = () => {
    dispatch(endMaintenance({ remarks: endRemarks })).then((res) => {
      if (res?.payload?.success) {
        setEndModal(false);
        setEndRemarks('');
        reload();
      } else {
        // State changed under us (e.g. another admin ended it) — refresh.
        reload();
      }
    });
  };

  const columns = [
    {
      title: 'Started At',
      dataIndex: 'started_at',
      key: 'started_at',
      width: '170px',
      render: (value) => formatDateTime(value)
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true
    },
    {
      title: 'Countdown',
      dataIndex: 'countdown_minutes',
      key: 'countdown_minutes',
      width: '110px',
      render: (value) => formatMinutes(value)
    },
    {
      title: 'Logout At',
      dataIndex: 'logout_at',
      key: 'logout_at',
      width: '170px',
      render: (value) => formatDateTime(value)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '110px',
      render: (value) => (
        <Tag color={STATUS_TAGS[value]?.color}>
          {STATUS_TAGS[value]?.label || value}
        </Tag>
      )
    },
    {
      title: 'Started By',
      dataIndex: 'started_by',
      key: 'started_by',
      width: '110px'
    },
    {
      title: 'Ended By',
      dataIndex: 'ended_by',
      key: 'ended_by',
      width: '110px',
      render: (value) => value || '-'
    },
    {
      title: 'Ended At',
      dataIndex: 'ended_at',
      key: 'ended_at',
      width: '170px',
      render: (value) => formatDateTime(value)
    },
    {
      title: 'Sessions Ended',
      dataIndex: 'sessions_terminated_count',
      key: 'sessions_terminated_count',
      width: '130px',
      render: (value) => (value === null || value === undefined ? '-' : value)
    }
  ];

  const logColumns = [
    {
      title: 'Action',
      dataIndex: 'action_type',
      key: 'action_type',
      width: '150px',
      render: (value) => ACTION_LABELS[value] || value
    },
    {
      title: 'Change',
      key: 'change',
      render: (_, log) => {
        if (log.field === 'logout_at') {
          return log.from
            ? `Logout ${formatDateTime(log.from)} → ${formatDateTime(log.to)}`
            : `Logout at ${formatDateTime(log.to)}`;
        }
        return log.from || log.to ? `${log.from} → ${log.to}` : '-';
      }
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      render: (value) => value || '-'
    },
    {
      title: 'By',
      dataIndex: 'change_by',
      key: 'change_by',
      width: '110px'
    },
    {
      title: 'When',
      dataIndex: 'update_date',
      key: 'update_date',
      width: '170px',
      render: (value) => formatDateTime(value)
    }
  ];

  const statusTag = cutoffReached ? (
    <Tag color="red">Users logged out · sign-in blocked</Tag>
  ) : (
    <Tag color="orange">Countdown running</Tag>
  );

  return (
    <Layout sider={false}>
      <Card
        title={<span>Maintenance Mode</span>}
        className={`${styles.cardStyles_approver}`}
        loading={getCurrentLoading && !current}
      >
        {window_ ? (
          <>
            <Alert
              className="mb-4"
              showIcon
              type={cutoffReached ? 'error' : 'warning'}
              message={
                cutoffReached
                  ? 'Maintenance is in progress. All users except admins are logged out and cannot sign in.'
                  : 'Countdown running. Every logged-in user sees a banner and will be logged out when it reaches zero.'
              }
              description={
                cutoffReached
                  ? 'End maintenance once the deployment is complete so users can sign in again.'
                  : null
              }
            />
            <Row gutter={24} className="mb-4">
              <Col xs={24} md={8}>
                <Statistic
                  title={
                    cutoffReached ? 'Logged out at' : 'Users logged out in'
                  }
                  value={
                    cutoffReached
                      ? formatClockTime(window_.logout_at)
                      : formatCountdown(remaining)
                  }
                />
              </Col>
              <Col xs={24} md={8}>
                <Statistic
                  title={
                    cutoffReached
                      ? 'Sessions ended'
                      : 'Users currently logged in'
                  }
                  value={
                    cutoffReached
                      ? window_.sessions_terminated_count ?? 'Pending'
                      : current?.logged_in_count || 0
                  }
                />
              </Col>
            </Row>
            <Descriptions bordered size="small" column={1} className="mb-4">
              <Descriptions.Item label="Status">{statusTag}</Descriptions.Item>
              <Descriptions.Item label="Reason">
                {window_.reason}
              </Descriptions.Item>
              <Descriptions.Item label="Started">
                {formatDateTime(window_.started_at)} by {window_.started_by}
              </Descriptions.Item>
              <Descriptions.Item label="Logout at">
                {formatDateTime(window_.logout_at)}
              </Descriptions.Item>
            </Descriptions>
            <Space wrap>
              {!cutoffReached ? (
                <CustomButton
                  className={styles.inwardButton}
                  onClick={onExtend}
                  loading={updateLoading}
                >
                  {`Extend by ${formatMinutes(extendStep)}`}
                </CustomButton>
              ) : null}
              <CustomButton
                className={styles.inwardButton}
                type="primary"
                danger={!cutoffReached}
                onClick={() => setEndModal(true)}
                loading={updateLoading}
              >
                {cutoffReached ? 'End Maintenance' : 'Cancel Maintenance'}
              </CustomButton>
            </Space>
          </>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={onStart}
            initialValues={{ countdown_minutes: DEFAULT_COUNTDOWN_MINUTES }}
          >
            <Alert
              className="mb-4"
              showIcon
              type="info"
              message="Starting maintenance shows a countdown to every logged-in user. When it reaches zero, all users except admins are logged out and cannot sign in until you end maintenance."
            />
            <Statistic
              className="mb-4"
              title="Users currently logged in"
              value={current?.logged_in_count || 0}
            />
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <FormItem
                  label="Reason"
                  name="reason"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: 'Please enter the reason for maintenance'
                    }
                  ]}
                >
                  <TextArea
                    rows={3}
                    maxLength={REASON_MAX_LENGTH}
                    showCount
                    placeholder="e.g. Release 3.2 deployment"
                  />
                </FormItem>
                <Text type="secondary">
                  Shown to every user, including on the sign-in page. Do not
                  include internal details.
                </Text>
              </Col>
              <Col xs={24} md={12}>
                <FormItem
                  label="Log users out after"
                  name="countdown_minutes"
                  rules={[{ required: true, message: 'Required field' }]}
                >
                  <Radio.Group buttonStyle="solid">
                    {countdownOptions.map((minutes) => (
                      <Radio.Button key={minutes} value={minutes}>
                        {formatMinutes(minutes)}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                </FormItem>
                <Text type="secondary">
                  Pick enough time for dispensing and eBMR steps in progress to
                  finish.
                </Text>
              </Col>
            </Row>
            <FormItem className="mt-4">
              <CustomButton
                className={styles.inwardButton}
                type="primary"
                htmlType="submit"
                loading={updateLoading}
              >
                Start Maintenance
              </CustomButton>
            </FormItem>
          </Form>
        )}
      </Card>

      <Card
        title={<span>Maintenance History</span>}
        className={`mt-4 ${styles.cardStyles_approver}`}
      >
        <div className={`${styles.table_container}`}>
          <CustomTable
            columns={columns}
            dataSource={history}
            rowKey="id"
            loading={getHistoryLoading}
            scroll={{ x: 'max-content' }}
            expandable={{
              expandedRowRender: (record) => (
                <CustomTable
                  columns={logColumns}
                  dataSource={record.change_logs}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              ),
              rowExpandable: (record) => !!record.change_logs?.length
            }}
            showPaginationMeta
            pagination={{
              size: 'small',
              total: pagination?.total_records || 0,
              current: page,
              pageSize,
              onChange
            }}
          />
        </div>
      </Card>

      {endModal ? (
        <CustomModal
          title={cutoffReached ? 'End Maintenance' : 'Cancel Maintenance'}
          visible={endModal}
          width={480}
          footer={null}
          closable={false}
          maskClosable={false}
        >
          <p>
            {cutoffReached
              ? 'Users will be able to sign in again immediately.'
              : 'The countdown stops and nobody is logged out.'}
          </p>
          <FormItem label="Remarks (optional)">
            <TextArea
              rows={3}
              maxLength={REASON_MAX_LENGTH}
              value={endRemarks}
              onChange={(e) => setEndRemarks(e.target.value)}
              placeholder={
                cutoffReached
                  ? 'e.g. Deployment completed and verified'
                  : 'e.g. Deployment postponed'
              }
            />
          </FormItem>
          <Space>
            <CustomButton
              className={styles.inwardButton}
              type="primary"
              onClick={onEnd}
              loading={updateLoading}
            >
              {cutoffReached ? 'End Maintenance' : 'Cancel Maintenance'}
            </CustomButton>
            <CustomButton
              className={styles.inwardButton}
              onClick={() => setEndModal(false)}
              ghost
              type="primary"
            >
              Close
            </CustomButton>
          </Space>
        </CustomModal>
      ) : null}
    </Layout>
  );
};

export default MaintenanceMode;
